/**
 * GoogleCalendarSync — Sezione "Confronto Google Calendar ↔ GHL"
 *
 * Visualizzata in testa alla pagina Calendario.
 *
 * Comportamento:
 *  - Mostra lo stato della connessione GCal (connected/not connected)
 *  - Admin: bottone "Connetti Google Calendar" → redirect a /auth (OAuth)
 *  - Card admin-only (SEC-101): il confronto espone PII → bottone "Confronta ora"
 *    chiama /api/google-calendar/compare-with-ghl, mostra:
 *      • banner verde se 0 mancanti
 *      • banner rosso + tabella se mancano appuntamenti in GHL
 *  - Auto-fetch al mount se già connesso
 *
 * Match logic lato server: data + ora (±15min) + nome paziente (fuzzy).
 * Eventi senza paziente riconoscibile (riunioni interne, ferie) vengono
 * ignorati a monte.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  CalendarDays, ExternalLink, Link2, Loader2, RefreshCw, AlertTriangle,
  CheckCircle2, PlugZap, Unplug,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";

const API = `${window.location.origin}/api`;

const DAY_NAMES_IT = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dayName = DAY_NAMES_IT[d.getDay()];
    const dateStr = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    const timeStr = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return `${dayName} ${dateStr} ${timeStr}`;
  } catch {
    return iso;
  }
}

export default function GoogleCalendarSync({ isAdmin = false }) {
  const [status, setStatus] = useState({ connected: false, updated_at: null, loading: true });
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  // Evita loop di retry infinito dell'auto-confronto quando la chiamata
  // fallisce in modo persistente (timeout/500): auto-run UNA sola volta.
  const autoRanRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/google-calendar/status`, { withCredentials: true });
      setStatus({ ...data, loading: false });
      return data.connected;
    } catch {
      setStatus({ connected: false, loading: false });
      return false;
    }
  }, []);

  const runComparison = useCallback(async () => {
    setComparing(true);
    try {
      const { data } = await axios.get(`${API}/google-calendar/compare-with-ghl`, {
        withCredentials: true,
        timeout: 90000,
      });
      setComparison(data);
      if (data.missing_count === 0) {
        toast.success("Tutti gli appuntamenti GCal sono presenti in GHL ✓");
      } else {
        toast.warning(`${data.missing_count} appuntamenti GCal MANCANO in GHL`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Errore nel confronto";
      toast.error(detail);
      if (e.response?.status === 401) {
        // Token revocato → forziamo re-fetch dello status (passerà a "not connected")
        await fetchStatus();
      }
    } finally {
      setComparing(false);
    }
  }, [fetchStatus]);

  const connectGCal = () => {
    // Redirect "top-level" (no fetch) — Google richiede browser navigation completa
    window.location.href = `${API}/google-calendar/auth`;
  };

  const disconnectGCal = async () => {
    if (!window.confirm("Disconnettere Google Calendar? Per riconnetterti dovrai re-autorizzare.")) return;
    setDisconnecting(true);
    try {
      await axios.post(`${API}/google-calendar/disconnect`, {}, { withCredentials: true });
      toast.success("Google Calendar disconnesso");
      setStatus({ connected: false, loading: false });
      setComparison(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore disconnessione");
    } finally {
      setDisconnecting(false);
    }
  };

  // Mount: fetch status. Se la query string contiene `gcal_auth=success`
  // (redirect dal callback OAuth) mostriamo un toast di conferma.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal_auth") === "success") {
      toast.success("Google Calendar connesso ✓");
      // pulizia URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("gcal_auth") === "error") {
      toast.error("Connessione Google Calendar fallita");
      window.history.replaceState({}, "", window.location.pathname);
    }
    fetchStatus();
  }, [fetchStatus]);

  // Auto-run del confronto al mount SE già connesso. Solo UNA volta
  // (autoRanRef) così un errore persistente non innesca retry infiniti.
  useEffect(() => {
    if (status.connected && !autoRanRef.current) {
      autoRanRef.current = true;
      runComparison();
    }
  }, [status.connected, runComparison]);

  const missing = comparison?.missing_in_ghl || [];
  const hasMissing = missing.length > 0;

  // Tronca a 20 per evitare scroll infinito. L'utente vede "+ N altri…"
  const visibleMissing = useMemo(() => missing.slice(0, 20), [missing]);

  // SEC-101: il confronto GCal ↔ GHL espone PII ed è admin-only lato
  // backend. Nascondiamo l'intera card ai non-admin per evitare errori
  // 403 e la comparsa di un tool a cui non hanno accesso.
  if (!isAdmin) return null;

  return (
    <div
      className="bg-white rounded-3xl shadow-md p-6 mb-5 border border-slate-100"
      data-testid="gcal-sync-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-md">
            <CalendarDays className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">Confronto Google Calendar ↔ GHL</h2>
            <p className="text-xs text-slate-500">
              Verifica se ci sono appuntamenti in Google Calendar che mancano in GHL
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status.loading ? (
            <Badge variant="outline" className="text-slate-500"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Verifico…</Badge>
          ) : status.connected ? (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="gcal-status-connected">
              <Link2 className="w-3 h-3 mr-1" /> Connesso
            </Badge>
          ) : (
            <Badge variant="outline" className="text-amber-600 border-amber-300" data-testid="gcal-status-disconnected">
              Non connesso
            </Badge>
          )}
        </div>
      </div>

      {/* Action bar — varia in base allo stato e al ruolo */}
      {!status.loading && !status.connected && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <strong>Google Calendar non è ancora connesso.</strong><br />
              Connetti il calendario condiviso dello studio per abilitare il confronto.
            </div>
          </div>
          <Button
            onClick={connectGCal}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            data-testid="gcal-connect-btn"
          >
            <PlugZap className="w-4 h-4 mr-2" /> Connetti Google Calendar
          </Button>
        </div>
      )}

      {status.connected && (
        <div className="space-y-4">
          {/* Action bar connesso */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Button
              onClick={runComparison}
              disabled={comparing}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
              data-testid="gcal-compare-btn"
            >
              {comparing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {comparing ? "Confronto in corso…" : "Confronta ora"}
            </Button>
            <Button
              onClick={disconnectGCal}
              disabled={disconnecting}
              variant="outline"
              size="sm"
              className="text-slate-500 hover:text-rose-600 rounded-xl"
              data-testid="gcal-disconnect-btn"
            >
              {disconnecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Unplug className="w-3 h-3 mr-1" />}
              Disconnetti
            </Button>
          </div>

          {/* Risultato confronto */}
          {comparison && (
            <div className="space-y-3">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3" data-testid="gcal-stats">
                <StatBox label="GCal (futuri)" value={comparison.gcal_total} color="blue" />
                <StatBox label="GHL (futuri)" value={comparison.ghl_total} color="purple" />
                <StatBox
                  label="Mancanti in GHL"
                  value={comparison.missing_count}
                  color={hasMissing ? "rose" : "emerald"}
                  testid="gcal-missing-count"
                />
              </div>

              {/* Banner risultato */}
              {!hasMissing && (
                <div
                  className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3"
                  data-testid="gcal-all-ok-banner"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <div className="text-sm text-emerald-800">
                    <strong>Tutto allineato!</strong> Ogni appuntamento Google Calendar
                    risulta presente anche in GHL.
                  </div>
                </div>
              )}

              {hasMissing && (
                <div
                  className="rounded-xl bg-rose-50 border-2 border-rose-300 p-4 space-y-3"
                  data-testid="gcal-missing-banner"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-rose-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-base font-bold text-rose-800">
                        {missing.length} {missing.length === 1 ? "appuntamento" : "appuntamenti"} GCal non trovati in GHL
                      </h3>
                      <p className="text-xs text-rose-700 mt-0.5">
                        Match cercato per data+ora (±15 min) e nome paziente. Verifica se vanno aggiunti a GHL o se è un evento non clinico.
                      </p>
                    </div>
                  </div>
                  <div className="rounded-lg bg-white border border-rose-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-rose-100/60 text-rose-700 text-[10px] uppercase tracking-wider">
                          <th className="text-left px-3 py-2 font-semibold">Quando</th>
                          <th className="text-left px-3 py-2 font-semibold">Paziente</th>
                          <th className="text-left px-3 py-2 font-semibold">Titolo</th>
                          <th className="text-left px-3 py-2 font-semibold w-12">Link</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-rose-100">
                        {visibleMissing.map((ev) => (
                          <tr key={ev.gcal_event_id} data-testid={`gcal-missing-${ev.gcal_event_id}`} className="hover:bg-rose-50/30">
                            <td className="px-3 py-2 text-slate-700 tabular-nums whitespace-nowrap text-xs">
                              {formatDateTime(ev.start_iso)}
                            </td>
                            <td className="px-3 py-2 text-slate-800 font-medium">{ev.patient_name}</td>
                            <td className="px-3 py-2 text-slate-600 truncate max-w-[280px]" title={ev.title}>
                              {ev.title}
                            </td>
                            <td className="px-3 py-2">
                              {ev.html_link && (
                                <a
                                  href={ev.html_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-blue-600 hover:text-blue-700"
                                  title="Apri in Google Calendar"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {missing.length > visibleMissing.length && (
                      <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-rose-100 text-center">
                        +{missing.length - visibleMissing.length} altri appuntamenti non mostrati
                      </div>
                    )}
                  </div>
                </div>
              )}

              <p className="text-[10px] text-slate-400 text-right">
                Ultimo confronto: {comparison.compared_at ? formatDateTime(comparison.compared_at) : "—"}
              </p>
            </div>
          )}

          {!comparison && !comparing && (
            <p className="text-xs text-slate-500 italic">
              Premi &quot;Confronta ora&quot; per cercare appuntamenti mancanti in GHL.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color = "slate", testid }) {
  const colorMap = {
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    purple: "border-purple-200 bg-purple-50 text-purple-700",
    rose: "border-rose-300 bg-rose-50 text-rose-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div
      className={`rounded-xl border-2 ${colorMap[color] || colorMap.slate} px-3 py-2.5 text-center`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase font-semibold opacity-70 tracking-wider mb-0.5">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value ?? "—"}</div>
    </div>
  );
}
